import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Printer, ChevronRight, ChevronLeft, Award, CheckCircle } from "lucide-react";

// =========================================================================
// INLINED UI COMPONENTS
// =========================================================================
const Button = ({ children, onClick, className = "", variant = "default", size = "default" }) => {
  let baseStyle = "flex items-center justify-center font-medium transition-colors focus:outline-none ";
  if (variant === "outline") baseStyle += "border border-stone-200 text-stone-700 hover:bg-stone-100 ";
  else baseStyle += "bg-indigo-600 text-white hover:bg-indigo-700 ";
  if (size === "sm") baseStyle += "px-3 py-1.5 text-sm rounded-sm ";
  else baseStyle += "px-4 py-2 rounded-sm ";
  return <button onClick={onClick} className={baseStyle + className}>{children}</button>;
};

const Card = ({ children, className = "", style }) => (
  <div className={`border rounded-sm bg-white ${className}`} style={style}>
    {children}
  </div>
);

const PortalHeader = ({ right }) => (
  <header className="bg-stone-900 text-white px-6 py-4 flex justify-between items-center print:hidden">
    <div className="font-bold tracking-widest text-lg">GBPIET PORTAL</div>
    <div>{right}</div>
  </header>
);

const PortalFooter = () => (
  <footer className="bg-stone-900 text-stone-400 py-6 text-center text-xs print:hidden">
    &copy; {new Date().getFullYear()} Govind Ballabh Pant Institute of Engineering & Technology. All rights reserved.
  </footer>
);
// =========================================================================

const ROMAN_ORDER = ["VIII", "VII", "VI", "V", "IV", "III", "II", "I"];

export default function StudentResults() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [selectedSem, setSelectedSem] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("student_data");
    
    if (!raw) {
      nav("/student");
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      setData(parsed);

      const studentName = parsed?.student?.name || "Student";
      const roll = parsed?.student?.roll_no || "";
      const prevTitle = document.title;
      document.title = `Result - ${studentName}${roll ? " (" + roll + ")" : ""}`;
      return () => {
        document.title = prevTitle;
      };
    } catch (err) {
      nav("/student");
    }
  }, [nav]);

  if (!data) return null;
  const { student, results } = data;
  
  // Sort descending: Latest semester first (e.g., VIII down to I)
  const ordered = [...results].sort(
    (a, b) => ROMAN_ORDER.indexOf(a.semester) - ROMAN_ORDER.indexOf(b.semester)
  );

  // =========================================================================
  // DYNAMIC BATCH & BRANCH CALCULATION LOGIC
  // =========================================================================
  // Latest Branch: Find the first semester in our descending list that has a branch
  const derivedBranch = ordered.find(r => r.branch)?.branch || student.branch;
  
  // Earliest Batch: Reverse the list to search from oldest to newest
  const ascOrdered = [...ordered].reverse();
  const derivedBatch = ascOrdered.find(r => r.batch)?.batch || student.batch;
  // =========================================================================

  // Strict 2 Decimal Formatter with proper rounding
  const formatDec = (val) => {
    if (val === null || val === undefined || val === "" || val === "—" || val === "-") return "—";
    const num = parseFloat(val);
    return isNaN(num) ? val : num.toFixed(2);
  };

  // Check if student has reached the TRUE terminal semester for their specific program
  const progUpper = String(student.program || "").toUpperCase();
  const isShortProgram = progUpper.includes("MASTER") || progUpper.includes("M.TECH") || progUpper.includes("MCA");
  const requiredFinalSem = isShortProgram ? "IV" : "VIII";

  // Only get terminalResult if the exact required semester has been uploaded
  const terminalResult = ordered.find(r => r.semester === requiredFinalSem);

  const sumTotalMarks = (subjects) => {
    let a = 0, b = 0, hit = false;
    (subjects || []).forEach((s) => {
      const m = (s.total || "").match(/^(-?\d+)\s*\/\s*(\d+)$/);
      if (m) {
        a += parseInt(m[1], 10);
        b += parseInt(m[2], 10);
        hit = true;
      }
    });
    return hit ? `${a}/${b}` : "—";
  };

  const isCleared = (s) => s.back && !s.back_pending;

  const processSubjectGrade = (s, programName) => {
    const isGP = (s.name || "").toUpperCase().includes("GENERAL PROFICIENCY") || (s.code || "").toUpperCase().startsWith("GP");
    const isAudit = String(s.credits) === "0" || s.non_credit;
    let gradeVal = s.grade;
    let gpVal = s.grade_points;

    if (isAudit && !isGP) {
      const progUpper = String(programName || "").toUpperCase();
      const isBtech = progUpper.includes("B.TECH") || progUpper.includes("B. TECH") || progUpper.includes("BACHELOR");
      
      const extRaw = String(s.external || "").trim().toUpperCase();
      const gradeRaw = String(s.grade || "").trim().toUpperCase();
      const gpRaw = String(s.grade_points || "").trim().toUpperCase();
      
      if (extRaw.includes("NA")) {
        gradeVal = "-";
      } else {
        const extMatch = extRaw.match(/^(-?\d+)\s*\/\s*(\d+)$/);
        const totMatch = String(s.total || "").match(/^(-?\d+)\s*\/\s*(\d+)$/);
        
        if (totMatch) {
          let extPass = true;
          if (extMatch) {
            const extObt = parseInt(extMatch[1], 10);
            const extMax = parseInt(extMatch[2], 10);
            extPass = extMax > 0 ? (extObt / extMax) >= 0.30 : true;
          }
          
          const totObt = parseInt(totMatch[1], 10);
          const totMax = parseInt(totMatch[2], 10);
          const totPass = totMax > 0 ? (totObt / totMax) >= 0.40 : false;
          
          gradeVal = isBtech 
            ? ((extPass && totPass) ? "Audit Pass (AP)" : "Audit Fail (AF)")
            : ((extPass && totPass) ? "Qualified (Q)" : "Not Qualified (NQ)");
        } else {
          const combined = `${gradeRaw} ${gpRaw}`;
          if (combined.includes("NQ") || combined.includes("NOT") || combined.includes("FAIL") || combined.includes("AF")) {
            gradeVal = isBtech ? "Audit Fail (AF)" : "Not Qualified (NQ)";
          } else if (combined.includes("Q") || combined.includes("PASS") || combined.includes("AP")) {
            gradeVal = isBtech ? "Audit Pass (AP)" : "Qualified (Q)";
          } else {
            gradeVal = "-";
          }
        }
      }
    }
    
    if (isGP || isAudit) {
      gpVal = "—";
    }

    return { isGP, isAudit, gradeVal, gpVal };
  };

  const PrintProfile = () => (
    <div className="print-profile hidden print:block mb-6 pb-4 border-b-2 border-stone-800">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[12px] uppercase tracking-widest text-stone-600 font-bold">
            GBPIET · Govind Ballabh Pant Institute of Engineering &amp; Technology
          </p>
          <h1 className="font-display text-3xl mt-2 text-stone-900">
            {student.name || "Student"}
          </h1>
        </div>
        <div className="text-right font-mono text-[12px] leading-relaxed text-stone-800">
          <p>Roll No: <span className="font-bold">{student.roll_no}</span></p>
          <p>Enrollment: {student.enroll_no || "—"}</p>
          <p>Batch: {derivedBatch}</p>
          <p className="font-bold uppercase">{student.program} ({derivedBranch})</p>
        </div>
      </div>
    </div>
  );

  const ASC_SEMS = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];
  const cumBySem = {};
  let _runningEC = 0;
  ASC_SEMS.forEach((s) => {
    const r = ordered.find((x) => x.semester === s);
    if (r) {
      _runningEC += parseInt(r.earned_credits, 10) || 0;
      cumBySem[s] = _runningEC;
    }
  });

  return (
    <div className="min-h-screen bg-stone-100 print:bg-white flex flex-col print:block">
      <PortalHeader
        right={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => window.print()} className="rounded-sm shadow-sm bg-white">
              <Printer className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Print Results</span>
            </Button>
            <Button size="sm" variant="outline" onClick={() => { sessionStorage.removeItem("student_data"); nav("/student"); }} className="rounded-sm shadow-sm bg-white text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
              <LogOut className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Sign out</span>
            </Button>
          </div>
        }
      />

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-12 space-y-6 md:space-y-10 flex-1 w-full print:p-0 print:space-y-0 print:block">
        <PrintProfile />

        <Card className="hidden md:flex border-stone-200 px-6 py-4 shadow-none flex-wrap items-center gap-6 text-xs font-mono print:hidden">
          <div className="flex items-center gap-2">
            <span className="text-amber-600 font-bold text-base">*</span>
            <span className="text-stone-700">subject cleared after back paper</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-stone-100 border border-stone-200 rounded-sm text-stone-700 font-semibold">$</span>
            <span className="text-stone-700">non-credit subject</span>
          </div>
        </Card>

        {!selectedSem && (
          <>
            <Card className="border-stone-200 p-6 md:p-10 shadow-sm paper-surface fade-up print:hidden">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Student profile</p>
              <h2 className="font-display text-3xl md:text-5xl mt-2 md:mt-3 leading-tight">{student.name}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-6 font-mono text-sm">
                <div><p className="text-[10px] md:text-xs text-stone-500 uppercase tracking-widest">Roll No.</p><p className="font-semibold text-stone-900">{student.roll_no}</p></div>
                <div><p className="text-[10px] md:text-xs text-stone-500 uppercase tracking-widest">Enrollment</p><p className="break-words font-semibold text-stone-900">{student.enroll_no || "—"}</p></div>
                <div><p className="text-[10px] md:text-xs text-stone-500 uppercase tracking-widest">Current Branch</p><p className="text-xs font-semibold text-stone-900">{derivedBranch}</p></div>
                <div><p className="text-[10px] md:text-xs text-stone-500 uppercase tracking-widest">Batch</p><p className="font-semibold text-stone-900">{derivedBatch}</p></div>
              </div>
            </Card>

            {/* Overview Page Final Academic Award Block (Always kept at top) */}
            {terminalResult && (
              <div className="bg-slate-900 text-white p-1 rounded-sm shadow-md mt-6 print:hidden fade-up">
                <div className="bg-slate-800 p-6 md:p-8 rounded-sm">
                  <h2 className="text-xl md:text-2xl font-display mb-6 flex items-center gap-2 text-white">
                    <Award className="text-amber-400" /> Final Academic Award
                  </h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="border border-slate-600 bg-slate-700/50 p-4 rounded-sm shadow-inner">
                      <span className="text-slate-400 text-[10px] block mb-1 font-semibold tracking-wider uppercase">Final CGPA</span>
                      <span className="text-2xl font-bold text-white">{formatDec(terminalResult.final_cgpa || terminalResult.cgpa)}</span>
                    </div>
                    <div className="border border-slate-600 bg-slate-700/50 p-4 rounded-sm shadow-inner">
                      <span className="text-slate-400 text-[10px] block mb-1 font-semibold tracking-wider uppercase">Final Result</span>
                      <span className={`text-xl font-bold ${String(terminalResult.final_result || terminalResult.result).toUpperCase().includes('PASS') ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {(terminalResult.final_result || terminalResult.result) ? String(terminalResult.final_result || terminalResult.result).toUpperCase() : "—"}
                      </span>
                    </div>
                    <div className="border border-slate-600 bg-slate-700/50 p-4 rounded-sm shadow-inner">
                      <span className="text-slate-400 text-[10px] block mb-1 font-semibold tracking-wider uppercase">Total Credits</span>
                      <span className="text-2xl font-bold text-white">{terminalResult.cuml_earned_credits || cumBySem[terminalResult.semester] || "—"}</span>
                    </div>
                    <div className="border border-slate-600 bg-slate-700/50 p-4 rounded-sm shadow-inner flex flex-col justify-center">
                      <span className="text-slate-400 text-[10px] block mb-1 font-semibold tracking-wider uppercase">Division Awarded</span>
                      <span className="text-sm font-bold text-amber-400 uppercase leading-snug">
                        {terminalResult.division || "FIRST DIVISION"}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-slate-400 mt-6 pt-4 border-t border-slate-600 flex items-center gap-2">
                    <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />
                    <span>Note: Conversion formula for CGPA into percentage is: Percentage = CGPA × 10</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {ordered.length === 0 && (
          <Card className="p-10 text-center border-stone-200 rounded-sm print:hidden">
            <p className="font-display text-2xl text-stone-400">No results recorded yet</p>
          </Card>
        )}

        {/* ========================================================= */}
        {/* ==================== MOBILE UX START ==================== */}
        {/* ========================================================= */}
        <div className="md:hidden block print:hidden">
          {!selectedSem && ordered.length > 0 && (
            <div className="fade-up mt-6">
              <h3 className="font-display text-2xl text-stone-800 mb-4 px-1">Academic Record</h3>
              <div className="space-y-3">
                {ordered.map((r) => (
                  <div 
                    key={r.semester} 
                    onClick={() => window.scrollTo(0, 0) || setSelectedSem(r.semester)}
                    className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm flex items-center justify-between active:bg-stone-50 transition-colors cursor-pointer"
                  >
                    <div>
                      <p className="font-bold text-lg text-stone-900 leading-none">Sem {r.semester}</p>
                      <p className="text-[10px] text-stone-500 font-mono mt-1 mb-2">{r.exam_session}</p>
                      <div className="flex gap-4 font-mono text-xs">
                        <p><span className="text-stone-400">SGPA:</span> <b className="text-stone-700">{formatDec(r.sgpa)}</b></p>
                        <p><span className="text-stone-400">CGPA:</span> <b className="text-stone-700">{formatDec(r.cgpa)}</b></p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm shadow-sm ${r.result?.toLowerCase().includes("pass") ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-amber-100 text-amber-800 border border-amber-200"}`}>
                        {r.result || "—"}
                      </span>
                      <ChevronRight className="w-5 h-5 text-stone-300" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedSem && (
            <div className="fade-up">
              <button 
                onClick={() => setSelectedSem(null)}
                className="flex items-center text-indigo-700 font-medium text-sm mb-4 bg-indigo-50 px-3 py-1.5 rounded-sm hover:bg-indigo-100 transition-colors w-full justify-center"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to all semesters
              </button>

              {ordered.filter(r => r.semester === selectedSem).map(r => {
                const hasDetailedMarks = (r.subjects || []).some(s => s.external || s.sessional);
                
                return (
                  <div key={r.semester} className="space-y-4">
                    <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                      <div className="px-4 py-3 bg-stone-50 border-b border-stone-100 flex justify-between items-center">
                        <div>
                          <h3 className="font-display text-2xl text-stone-900 leading-none">Sem {r.semester}</h3>
                          <p className="text-[10px] text-stone-500 font-mono mt-1">{r.exam_session}</p>
                        </div>
                        <span className={`px-2.5 py-1 text-xs font-bold uppercase rounded-sm shadow-sm ${r.result?.toLowerCase().includes("pass") ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-amber-100 text-amber-800 border border-amber-200"}`}>
                          {r.result || "—"}
                        </span>
                      </div>
                      
                      <div className="p-4 grid grid-cols-2 gap-y-4 gap-x-2 text-sm">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-0.5 font-semibold">SGPA / CGPA</p>
                          <p className="font-mono font-bold text-stone-900 text-lg">{formatDec(r.sgpa)} <span className="text-stone-300 mx-1 font-normal text-sm">/</span> {formatDec(r.cgpa)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-0.5 font-semibold">Credits / Cum.</p>
                          <p className="font-mono font-bold text-stone-900 text-lg">{r.earned_credits || "—"} <span className="text-stone-300 mx-1 font-normal text-sm">/</span> {cumBySem[r.semester] || "—"}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-0.5 font-semibold">Total Marks</p>
                          <p className="font-mono font-bold text-stone-900 text-base">
                            {r.total_marks_obtained ? `${r.total_marks_obtained}/${r.total_max_marks}` : sumTotalMarks(r.subjects)}
                          </p>
                        </div>
                      </div>
                      {r.remark && (
                        <div className="px-4 py-3 bg-red-50 border-t border-red-100">
                          <p className="text-[10px] uppercase tracking-wider text-red-600 mb-0.5 font-semibold">Remark</p>
                          <p className="text-xs font-mono text-red-800 font-semibold leading-relaxed">{r.remark}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-4 px-1 text-[10px] font-mono text-stone-500">
                      <span><b className="text-amber-600">*</b> Back Paper Cleared</span>
                      <span><b className="text-stone-700">$</b> Non-Credit</span>
                    </div>

                    <div className="space-y-3">
                      {(r.subjects || []).map((s, i) => {
                        const { isGP, isAudit, gradeVal, gpVal } = processSubjectGrade(s, r.program);
                        
                        return (
                          <div key={i} className={`bg-white border rounded-sm p-4 shadow-sm ${isCleared(s) ? 'border-amber-300 bg-amber-50/50' : 'border-stone-200'}`}>
                            <p className="text-[10px] text-stone-400 font-mono mb-0.5">{s.code}</p>
                            <p className="font-bold text-stone-900 leading-snug mb-3 text-[15px]">
                              {isCleared(s) ? <span className="text-amber-800">{s.name}</span> : s.name}
                            </p>
                            
                            <div className="flex justify-between items-center border-t border-stone-100 pt-3">
                              {!(isGP || isAudit) && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-0.5">Cr / GP</p>
                                  <p className="font-mono text-sm text-stone-800 font-medium">{s.credits} <span className="text-stone-300 mx-1">/</span> {gpVal}</p>
                                </div>
                              )}
                              {(isGP || isAudit) && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-0.5">Credits</p>
                                  <p className="font-mono text-sm text-stone-800 font-medium">{s.credits}</p>
                                </div>
                              )}
                              <div className="text-right">
                                <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-0.5">Grade</p>
                                <p className="text-xl font-bold leading-none text-indigo-900">{gradeVal}</p>
                              </div>
                            </div>

                            {hasDetailedMarks && (
                              <div className="flex justify-between items-center mt-3 bg-stone-50 border border-stone-200 rounded-sm p-2 text-center divide-x divide-stone-200">
                                <div className="flex-1 px-1">
                                  <p className="text-[9px] uppercase tracking-wider text-stone-500 mb-0.5 font-bold">Ext</p>
                                  <p className="font-mono text-xs text-stone-800">{s.external === "0/0" ? "-" : (s.external || "—")}</p>
                                </div>
                                <div className="flex-1 px-1">
                                  <p className="text-[9px] uppercase tracking-wider text-stone-500 mb-0.5 font-bold">Int</p>
                                  <p className="font-mono text-xs text-stone-800">{s.sessional || "—"}</p>
                                </div>
                                <div className="flex-1 px-1">
                                  <p className="text-[9px] uppercase tracking-wider text-stone-500 mb-0.5 font-bold">Total</p>
                                  <p className="font-mono text-xs font-bold text-stone-900">{s.total || "—"}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="pt-4 pb-10">
                      <button 
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                          setSelectedSem(null);
                        }}
                        className="w-full flex items-center justify-center gap-2 bg-white border-2 border-indigo-200 text-indigo-800 py-4 rounded-sm font-semibold shadow-sm hover:bg-indigo-50 transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5 text-indigo-500" />
                        Back to all semesters
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ========================================================= */}
        {/* ================= DESKTOP & PRINT UX ==================== */}
        {/* ========================================================= */}
        <div className="hidden md:block print:block print:w-full">
          
          {/* Summary Table: Prints on Page 1, then triggers page break */}
          {ordered.length > 0 && (
            <Card className="bg-white border-stone-200 p-10 rounded-sm shadow-sm fade-up print:shadow-none print:border-none print:p-0 mb-10 print:m-0 print:block print:[page-break-after:always]">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold print:hidden mb-1">Academic summary</p>
              <h3 className="font-display text-3xl print:text-2xl print:mb-2 text-stone-800">Semester-wise overview</h3>
              
              <div className="mt-6 overflow-x-auto print:overflow-visible print:mt-2">
                <table className="w-full text-sm print:text-[11px] border border-stone-200 rounded-sm">
                  <thead className="bg-stone-50">
                    <tr className="text-xs uppercase tracking-widest text-stone-500 border-b border-stone-200 print:text-[9px]">
                      <th className="text-left p-3 font-semibold">Semester</th>
                      <th className="text-left p-3 font-semibold">Session</th>
                      <th className="text-right p-3 font-semibold">SGPA</th>
                      <th className="text-right p-3 font-semibold">CGPA</th>
                      <th className="text-right p-3 font-semibold">Total Marks</th>
                      <th className="text-right p-3 font-semibold">Earned Cr.</th>
                      <th className="text-right p-3 font-semibold">Cum. Cr.</th>
                      <th className="text-left p-3 font-semibold">Result</th>
                      <th className="text-left p-3 font-semibold">Remark</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-stone-800">
                    {ordered.map((r) => (
                      <tr key={r.semester} className="border-t border-stone-200 hover:bg-stone-50 print:break-inside-avoid">
                        <td className="p-3 font-semibold bg-stone-50/50">Sem {r.semester}</td>
                        <td className="p-3 text-xs text-stone-600">{r.exam_session}</td>
                        <td className="p-3 text-right font-semibold">{formatDec(r.sgpa)}</td>
                        <td className="p-3 text-right font-semibold">{formatDec(r.cgpa)}</td>
                        <td className="p-3 text-right">
                          {r.total_marks_obtained ? `${r.total_marks_obtained}/${r.total_max_marks}` : sumTotalMarks(r.subjects)}
                        </td>
                        <td className="p-3 text-right">{r.earned_credits || "—"}</td>
                        <td className="p-3 text-right">{cumBySem[r.semester] || "—"}</td>
                        <td className={`p-3 font-semibold ${r.result?.toLowerCase().includes("pass") ? "text-emerald-700" : "text-amber-800"}`}>
                          {r.result ? String(r.result).toUpperCase() : "—"}
                        </td>
                        <td className="p-3 text-red-600 font-semibold min-w-[120px]">
                          {r.remark || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Print Final Award Block safely appended below summary table */}
              {terminalResult && (
                <div className="hidden print:block mt-8 border-2 border-stone-800 rounded-sm print:break-inside-avoid">
                  <div className="bg-stone-200 text-stone-900 p-3 font-bold flex items-center gap-2">
                     <span className="text-sm tracking-wide uppercase">Final Degree Award Validation</span>
                  </div>
                  <div className="p-4 grid grid-cols-4 gap-4">
                    <div>
                      <span className="text-stone-500 text-[10px] font-bold uppercase tracking-wider block mb-1">FINAL CGPA</span>
                      <span className="text-lg font-extrabold text-stone-900">{formatDec(terminalResult.final_cgpa || terminalResult.cgpa)}</span>
                    </div>
                    <div>
                      <span className="text-stone-500 text-[10px] font-bold uppercase tracking-wider block mb-1">FINAL RESULT</span>
                      <span className="text-lg font-extrabold text-stone-900">{(terminalResult.final_result || terminalResult.result) ? String(terminalResult.final_result || terminalResult.result).toUpperCase() : "—"}</span>
                    </div>
                    <div>
                      <span className="text-stone-500 text-[10px] font-bold uppercase tracking-wider block mb-1">TOTAL CREDITS</span>
                      <span className="text-lg font-extrabold text-stone-900">{terminalResult.cuml_earned_credits || cumBySem[terminalResult.semester] || "—"}</span>
                    </div>
                    <div>
                      <span className="text-stone-500 text-[10px] font-bold uppercase tracking-wider block mb-1">DIVISION</span>
                      <span className="text-xs font-extrabold text-stone-900 mt-1 block leading-snug">{terminalResult.division || "FIRST DIVISION"}</span>
                    </div>
                  </div>
                  <div className="bg-stone-50 p-2 text-center text-[10px] font-medium text-stone-600 border-t border-stone-200">
                    Note: Conversion formula for CGPA into percentage is: Percentage = CGPA × 10
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Detailed Semesters: Each one is strictly isolated to its own printed page */}
          {ordered.map((r, idx) => {
            const hasDetailedMarks = (r.subjects || []).some(s => s.external || s.sessional);

            return (
              <Card
                key={r.semester}
                className="bg-white border-stone-200 p-10 rounded-sm shadow-sm fade-up mb-10 print:shadow-none print:border-none print:p-0 print:m-0 print:block print:[page-break-after:always] print:[page-break-inside:avoid]"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className="flex items-end justify-between flex-wrap gap-4 border-b border-stone-200 pb-4 print:pb-2">
                  <div>
                    <h3 className="font-display text-3xl print:text-2xl text-stone-800">{r.semester} Semester</h3>
                    <p className="text-xs text-stone-500 font-mono mt-1">{r.exam_session}</p>
                  </div>
                  
                  <div className="text-right font-mono text-sm space-y-1 print:text-xs">
                    <p><span className="text-stone-500">SGPA:</span> <b className="text-stone-800">{formatDec(r.sgpa)}</b> <span className="text-stone-300 mx-1">|</span> <span className="text-stone-500">CGPA:</span> <b className="text-stone-800">{formatDec(r.cgpa)}</b></p>
                    <p><span className="text-stone-500">Total:</span> <b className="text-stone-800">{r.total_marks_obtained ? `${r.total_marks_obtained}/${r.total_max_marks}` : sumTotalMarks(r.subjects)}</b></p>
                    <p><span className="text-stone-500">Credits:</span> <b className="text-stone-800">{r.earned_credits || "—"}</b> <span className="text-stone-300 mx-1">|</span> <span className="text-stone-500">Result:</span> <b className={`uppercase ${r.result?.toLowerCase().includes("pass") ? 'text-emerald-700' : 'text-amber-700'}`}>{r.result ? String(r.result).toUpperCase() : "—"}</b></p>
                    {r.remark && (
                      <p className="text-red-600 font-semibold max-w-md ml-auto">
                        <strong>Remark:</strong> {r.remark}
                      </p>
                    )}
                  </div>
                </div> 

                <div className="overflow-x-auto print:overflow-visible mt-6 print:mt-2">
                  <table className="w-full text-sm print:text-[11px] border border-stone-200">
                    <thead className="bg-stone-50">
                      <tr className="text-xs uppercase tracking-widest text-stone-500 border-b border-stone-200 print:text-[9px]">
                        <th className="text-left p-3 w-28 font-semibold">Code</th>
                        <th className="text-left p-3 font-semibold">Subject</th>
                        <th className="text-right p-3 w-12 font-semibold">Cr.</th>
                        {hasDetailedMarks && (
                          <>
                            <th className="text-right p-3 w-20 font-semibold">Ext.</th>
                            <th className="text-right p-3 w-20 font-semibold">Sess.</th>
                            <th className="text-right p-3 w-20 font-semibold">Tot.</th>
                          </>
                        )}
                        <th className="text-right p-3 w-16 font-semibold">Grade</th>
                        <th className="text-right p-3 w-16 font-semibold">GP</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono text-stone-800">
                      {(r.subjects || []).map((s, i) => {
                        const { isGP, isAudit, gradeVal, gpVal } = processSubjectGrade(s, r.program);
                        return (
                          <tr key={i} className={`border-t border-stone-200 hover:bg-stone-50 ${isCleared(s) ? "bg-amber-50 print:bg-transparent" : ""} print:break-inside-avoid`}>
                            <td className="p-3 bg-stone-50/50">{s.code}</td>
                            <td className="p-3 sans font-semibold text-stone-900">{isCleared(s) ? <span className="text-amber-800">{s.name}</span> : s.name}</td>
                            <td className="p-3 text-right">{s.credits}</td>
                            
                            {hasDetailedMarks && (
                              <>
                                <td className="p-3 text-right text-stone-600">{s.external === "0/0" ? "-" : (s.external || "—")}</td>
                                <td className="p-3 text-right text-stone-600">{s.sessional || "—"}</td>
                                <td className="p-3 text-right font-bold text-stone-900">{s.total || "—"}</td>
                              </>
                            )}
                            
                            {isGP || isAudit ? (
                              <td colSpan={2} className="p-3 text-center font-bold tracking-widest text-stone-500 bg-stone-100 print:bg-transparent">
                                {gradeVal}
                              </td>
                            ) : (
                              <>
                                <td className="p-3 text-right font-bold text-indigo-900 text-base">{gradeVal}</td>
                                <td className="p-3 text-right font-semibold">{gpVal}</td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {r.remark && <p className="hidden print:block mt-2 text-[11px] text-stone-500 font-mono italic">Remark: {r.remark}</p>}
              </Card>
            );
          })}
        </div>

      </main>
      
      <div className="print:hidden">
        <PortalFooter />
      </div>
    </div>
  );
}
